import { GridRowParams } from '@mui/x-data-grid/models/params/gridRowParams';
import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Edit } from '@mui/icons-material';
import { Tooltip, Chip } from '@mui/material';
import { ACLAuthorization, AccessMode } from '@/api/bol';

interface ACLAuthorizationColumnsProps {
  onEdit: (authorization: ACLAuthorization) => void;
  onDelete: (authorization: ACLAuthorization) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  editTooltip?: string;
  deleteTooltip?: string;
}

const getAccessModeColor = (mode: AccessMode): 'default' | 'primary' | 'success' | 'error' | 'info' | 'warning' => {
  switch (mode) {
    case 'READ':
      return 'info';
    case 'WRITE':
      return 'warning';
    case 'CONTROL':
      return 'success';
    default:
      return 'default';
  }
};

export const createACLAuthorizationColumns = ({
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  editTooltip = '',
  deleteTooltip = ''
}: ACLAuthorizationColumnsProps): GridColDef[] => [
  {
    field: 'memberIdentifier',
    headerName: 'Member Identifier',
    flex: 2,
    minWidth: 250,
    valueGetter: (_value: string | undefined, row: ACLAuthorization) => {
      return row.memberIdentifier || '-';
    },
  },
  {
    field: 'mode',
    headerName: 'Access Mode',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      const mode = params.row.mode as AccessMode;
      return (
        <Chip 
          label={mode} 
          color={getAccessModeColor(mode)} 
          size="small"
        />
      );
    },
  },
  {
    field: 'authorizedBy',
    headerName: 'Authorized By',
    flex: 2,
    minWidth: 250,
    valueGetter: (_value: string | undefined, row: ACLAuthorization) => {
      return row.authorizedBy || '-';
    },
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 100,
    hideable: false,
    getActions: (params: GridRowParams<ACLAuthorization>) => [
      <Tooltip title={!canEdit ? editTooltip : 'Edit authorization'} key="edit-tooltip">
        <span>
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            onClick={() => onEdit(params.row)}
            disabled={!canEdit}
            color="primary"
          />
        </span>
      </Tooltip>,
      <Tooltip title={!canDelete ? deleteTooltip : 'Delete authorization'} key="delete-tooltip">
        <span>
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => onDelete(params.row)}
            disabled={!canDelete}
            color="error"
          />
        </span>
      </Tooltip>,
    ],
  },
];

export const defaultACLAuthorizationColumnVisibilityModel: Record<string, boolean> = {
  memberIdentifier: true,
  mode: true,
  authorizedBy: true,
  actions: true,
};
